import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import type { BankMatchCandidate, InvoiceObligationRow, InvoiceObligationsPayload, ScheduledPaymentRow } from '../types/biApp';
import { api, formatApiError, mutate } from '../shared/api';
import { API_BASE } from '../shared/constants';
import { formatMoney, formatDate, formatDateTime, formatMoneyInput, parseMoneyInput } from '../shared/formatting';
import { matchesQuickSearch, QuickSearchInput } from '../shared/quickSearch';
import { adminSectionDefinition, adminSectionNavLabel } from '../shared/adminSectionRegistry';
import { AdminCreateCard } from '../shared/AdminCreateCard';
import { DateInput } from '../shared/DateInput';
import { OrderCodeField } from '../shared/OrderCodeField';
import { PartnerDrawer, type PartnerRow } from '../shared/PartnerDrawer';

type StatusTab = 'active' | 'archived' | 'all';
type InvoiceAdminSection = '1211' | '1212' | '1213';
type LedgerFilter = 'all' | 'not_booked' | 'booked' | 'partial';
type PaymentFilter = 'all' | 'unpaid' | 'paid' | 'partial';
type YesNoAll = 'all' | 'yes' | 'no';

type PaymentDraft = {
    key: string;
    serverId: number | null;
    planned_amount: string;
    planned_date: string;
    bank_statement_entry_id: number | null;
};

function lockedKindFromSection(s: InvoiceAdminSection): 'payable' | 'receivable' | 'standing_payment' {
    if (s === '1211') {
        return 'payable';
    }
    if (s === '1212') {
        return 'receivable';
    }

    return 'standing_payment';
}

function newDraftKey(): string {
    return `pay-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function AdminInvoiceObligationsSection({
    token,
    locale,
    reloadTick,
    invoiceAdminSection,
    onNavigateToCashflow,
}: {
    token: string;
    locale: 'hr' | 'en';
    reloadTick: number;
    invoiceAdminSection: InvoiceAdminSection;
    onNavigateToCashflow?: (opts: { companyIds: number[]; dateFrom?: string; dateTo?: string }) => void;
}) {
    const def = adminSectionDefinition(invoiceAdminSection);
    const title = def ? adminSectionNavLabel(def, locale) : invoiceAdminSection;
    const lockedKind = useMemo(() => lockedKindFromSection(invoiceAdminSection), [invoiceAdminSection]);

    const [payload, setPayload] = useState<InvoiceObligationsPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusTab, setStatusTab] = useState<StatusTab>('active');
    const [ledgerFilter, setLedgerFilter] = useState<LedgerFilter>('all');
    const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>('all');
    const [liquidatedFilter, setLiquidatedFilter] = useState<YesNoAll>('all');
    const [hasPoFilter, setHasPoFilter] = useState<YesNoAll>('all');
    const [invoiceTableSearch, setInvoiceTableSearch] = useState('');
    // Default raspon: zadnjih 90 dana (otprilike trenutni period); default overdue_unpaid true (1212/IRA samo).
    const today = new Date().toISOString().slice(0, 10);
    const ninetyAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
    const [detectedDateFrom, setDetectedDateFrom] = useState<string>(ninetyAgo);
    const [detectedDateTo, setDetectedDateTo] = useState<string>(today);
    const [detectedOverdueUnpaid, setDetectedOverdueUnpaid] = useState<boolean>(invoiceAdminSection === '1212');
    const [saving, setSaving] = useState(false);

    const [newVirmanNote, setNewVirmanNote] = useState('');

    const [newCompanyId, setNewCompanyId] = useState('');
    const [newCounterpartyId, setNewCounterpartyId] = useState('');
    const [newIncomingInvoiceId, setNewIncomingInvoiceId] = useState('');
    const [newDocNum, setNewDocNum] = useState('');
    const [newDocDate, setNewDocDate] = useState('');
    const [newDueDate, setNewDueDate] = useState('');
    const [newAmountTotal, setNewAmountTotal] = useState('');
    const [newAmountOpen, setNewAmountOpen] = useState('');
    const [newCostType, setNewCostType] = useState('OPEX');
    const [newNotes, setNewNotes] = useState('');
    const [newPurchaseOrderCode, setNewPurchaseOrderCode] = useState('');

    const [importCompanyId, setImportCompanyId] = useState('');
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    const [importMessage, setImportMessage] = useState('');

    const [editId, setEditId] = useState<number | null>(null);
    const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
    const [editCompanyId, setEditCompanyId] = useState('');
    const [editCounterpartyId, setEditCounterpartyId] = useState('');
    const [partnerEditor, setPartnerEditor] = useState<PartnerRow | 'new' | null>(null);
    const [editIncomingInvoiceId, setEditIncomingInvoiceId] = useState('');
    const [editDocNum, setEditDocNum] = useState('');
    const [editDocDate, setEditDocDate] = useState('');
    const [editDueDate, setEditDueDate] = useState('');
    const [editAmountTotal, setEditAmountTotal] = useState('');
    const [editAmountOpen, setEditAmountOpen] = useState('');
    const [editCostType, setEditCostType] = useState('');
    const [editNotes, setEditNotes] = useState('');
    const [editStatus, setEditStatus] = useState<'open' | 'archived'>('open');
    const [editVirmanNote, setEditVirmanNote] = useState('');
    const [editPurchaseOrderCode, setEditPurchaseOrderCode] = useState('');
    const [editLedgerStatus, setEditLedgerStatus] = useState<'not_booked' | 'booked' | 'partial'>('not_booked');
    const [editPaymentStatus, setEditPaymentStatus] = useState<'unpaid' | 'paid' | 'partial'>('unpaid');
    const [editLiquidated, setEditLiquidated] = useState(false);

    const [paymentDrafts, setPaymentDrafts] = useState<PaymentDraft[]>([]);
    const [candidatesByKey, setCandidatesByKey] = useState<Record<string, BankMatchCandidate[]>>({});

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const q = new URLSearchParams();
            q.set('status', statusTab);
            q.set('kind', lockedKind);
            if (ledgerFilter !== 'all') {
                q.set('ledger_status', ledgerFilter);
            }
            if (paymentFilter !== 'all') {
                q.set('payment_status', paymentFilter);
            }
            if (liquidatedFilter !== 'all') {
                q.set('liquidated', liquidatedFilter);
            }
            if (hasPoFilter !== 'all') {
                q.set('has_po', hasPoFilter);
            }
            if (detectedDateFrom) q.set('date_from', detectedDateFrom);
            if (detectedDateTo) q.set('date_to', detectedDateTo);
            if (detectedOverdueUnpaid) q.set('overdue_unpaid', '1');
            const res = await api<InvoiceObligationsPayload>(`/admin/invoice-obligations?${q.toString()}`, token);
            setPayload(res);
            setNewCompanyId((c) => c || String(res.companies[0]?.id ?? ''));
            setImportCompanyId((c) => c || String(res.companies[0]?.id ?? ''));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
            setPayload(null);
        } finally {
            setLoading(false);
        }
    }, [token, statusTab, lockedKind, ledgerFilter, paymentFilter, liquidatedFilter, hasPoFilter, detectedDateFrom, detectedDateTo, detectedOverdueUnpaid]);

    useEffect(() => {
        void load();
    }, [load, reloadTick]);

    function paymentDraftsFromServer(rows: ScheduledPaymentRow[]): PaymentDraft[] {
        if (rows.length === 0) {
            return [];
        }

        return rows.map((p) => ({
            key: `srv-${p.id}`,
            serverId: p.id,
            planned_amount: formatMoneyInput(p.planned_amount),
            planned_date: p.planned_date ? p.planned_date.slice(0, 10) : '',
            bank_statement_entry_id: p.bank_statement_entry_id,
        }));
    }

    async function startEdit(row: InvoiceObligationRow) {
        setEditId(row.id);
        setEditCompanyId(String(row.company_id));
        setEditCounterpartyId(row.counterparty_id ? String(row.counterparty_id) : '');
        setEditIncomingInvoiceId(row.incoming_invoice_id ? String(row.incoming_invoice_id) : '');
        setEditDocNum(row.document_number ?? '');
        setEditDocDate(row.document_date ? row.document_date.slice(0, 10) : '');
        setEditDueDate(row.due_date ? row.due_date.slice(0, 10) : '');
        setEditAmountTotal(formatMoneyInput(row.amount_total));
        setEditAmountOpen(formatMoneyInput(row.amount_open));
        setEditCostType(row.cost_type ?? 'OPEX');
        setEditNotes(row.notes ?? '');
        setEditStatus(row.status === 'archived' ? 'archived' : 'open');
        const m = row.meta && typeof row.meta === 'object' ? row.meta : {};
        setEditVirmanNote(typeof m.purpose === 'string' ? m.purpose : '');
        setEditPurchaseOrderCode(row.purchase_order_public_code ?? '');
        const ls = row.ledger_status;
        setEditLedgerStatus(ls === 'booked' || ls === 'partial' ? ls : 'not_booked');
        const ps = row.payment_status;
        setEditPaymentStatus(ps === 'paid' || ps === 'partial' ? ps : 'unpaid');
        setEditLiquidated(Boolean(row.is_liquidated));
        setCandidatesByKey({});

        try {
            const payRes = await api<{ payments: ScheduledPaymentRow[] }>(
                `/admin/invoice-obligations/${row.id}/scheduled-payments`,
                token,
            );
            setPaymentDrafts(paymentDraftsFromServer(payRes.payments));
        } catch {
            setPaymentDrafts([]);
        }
    }

    async function postImport(e: FormEvent) {
        e.preventDefault();
        if (!importFile || !importCompanyId || importing) {
            return;
        }
        setImporting(true);
        setImportMessage('');
        setError('');
        try {
            const fd = new FormData();
            fd.append('file', importFile);
            fd.append('company_id', importCompanyId);
            fd.append('obligation_kind', lockedKind);

            const response = await fetch(`${API_BASE}/admin/invoice-obligations/import`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: fd,
            });
            const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

            if (response.status === 409 && data.duplicate === true && typeof data.existing_id === 'number') {
                setImportMessage(
                    locale === 'en'
                        ? `Duplicate — obligation #${data.existing_id} already exists (same dedup key).`
                        : `Duplikat — obveza #${data.existing_id} već postoji (isti ključ deduplikacije).`,
                );

                return;
            }

            if (!response.ok) {
                throw new Error(formatApiError(data));
            }

            setImportMessage(
                typeof data.message === 'string' && data.message !== ''
                    ? (data.message as string)
                    : locale === 'en' ? 'Imported successfully.' : 'Uvezeno.',
            );
            setImportFile(null);
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Import');
        } finally {
            setImporting(false);
        }
    }

    async function submitNew(e: FormEvent) {
        e.preventDefault();
        if (!newCompanyId || saving) {
            return;
        }
        setSaving(true);
        setError('');
        try {
            await mutate('/admin/invoice-obligations', token, 'POST', {
                company_id: Number(newCompanyId),
                counterparty_id: newCounterpartyId ? Number(newCounterpartyId) : null,
                incoming_invoice_id: newIncomingInvoiceId ? Number(newIncomingInvoiceId) : null,
                document_number: newDocNum || null,
                document_date: newDocDate || null,
                due_date: newDueDate || null,
                amount_total: Number(newAmountTotal),
                amount_open: Number(newAmountOpen),
                currency_code: 'EUR',
                cost_type: newCostType || null,
                notes: newNotes || null,
                obligation_kind: lockedKind,
                meta:
                    newVirmanNote.trim() !== '' || lockedKind === 'standing_payment'
                        ? {
                              ...(newVirmanNote.trim() ? { purpose: newVirmanNote.trim() } : {}),
                              ...(lockedKind === 'standing_payment' ? { workflow: 'virman' } : {}),
                          }
                        : null,
                purchase_order_public_code: newPurchaseOrderCode.trim(),
            });
            setNewDocNum('');
            setNewDocDate('');
            setNewDueDate('');
            setNewAmountTotal('');
            setNewAmountOpen('');
            setNewNotes('');
            setNewPurchaseOrderCode('');
            setNewVirmanNote('');
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        } finally {
            setSaving(false);
        }
    }

    async function submitEdit(e: FormEvent) {
        e.preventDefault();
        if (editId == null || saving) {
            return;
        }
        setSaving(true);
        setError('');
        try {
            await mutate(`/admin/invoice-obligations/${editId}`, token, 'PATCH', {
                company_id: Number(editCompanyId),
                counterparty_id: editCounterpartyId ? Number(editCounterpartyId) : null,
                incoming_invoice_id: editIncomingInvoiceId ? Number(editIncomingInvoiceId) : null,
                document_number: editDocNum || null,
                document_date: editDocDate || null,
                due_date: editDueDate || null,
                amount_total: parseMoneyInput(editAmountTotal) ?? 0,
                amount_open: parseMoneyInput(editAmountOpen) ?? 0,
                currency_code: 'EUR',
                cost_type: editCostType || null,
                notes: editNotes || null,
                status: editStatus,
                obligation_kind: lockedKind,
                meta:
                    editVirmanNote.trim() !== '' || lockedKind === 'standing_payment'
                        ? {
                              ...(editVirmanNote.trim() ? { purpose: editVirmanNote.trim() } : {}),
                              ...(lockedKind === 'standing_payment' ? { workflow: 'virman' } : {}),
                          }
                        : null,
                purchase_order_public_code: editPurchaseOrderCode.trim(),
                ledger_status: editLedgerStatus,
                payment_status: editPaymentStatus,
                is_liquidated: editLiquidated,
            });

            const paymentsPayload = paymentDrafts
                .filter((p) => p.planned_amount.trim() !== '' && p.planned_date.trim() !== '')
                .map((p, i) => ({
                    planned_amount: parseMoneyInput(p.planned_amount) ?? 0,
                    planned_date: p.planned_date,
                    sort_order: i,
                    bank_statement_entry_id: p.bank_statement_entry_id,
                }));

            await mutate(`/admin/invoice-obligations/${editId}/scheduled-payments`, token, 'PUT', {
                payments: paymentsPayload,
            });

            setEditId(null);
            setPaymentDrafts([]);
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        } finally {
            setSaving(false);
        }
    }

    async function loadCandidatesForDraft(key: string, amountStr: string, dateStr: string) {
        if (editId == null) {
            return;
        }
        const amount = parseMoneyInput(amountStr) ?? 0;
        if (!amount || !dateStr) {
            return;
        }
        setError('');
        try {
            const q = new URLSearchParams();
            q.set('amount', String(amount));
            q.set('date', dateStr);
            q.set('days', '21');
            const res = await api<{ candidates: BankMatchCandidate[] }>(
                `/admin/invoice-obligations/${editId}/bank-match-candidates?${q.toString()}`,
                token,
            );
            setCandidatesByKey((prev) => ({ ...prev, [key]: res.candidates }));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
        }
    }

    async function reconcileDraft(key: string, serverId: number | null, entryId: number) {
        if (editId == null || serverId == null) {
            setError(
                locale === 'en'
                    ? 'Save scheduled payments first so each line has an ID, then link to bank.'
                    : 'Prvo spremi planirana plaćanja kako bi svaki red dobio ID, zatim veži izvod.',
            );

            return;
        }
        setSaving(true);
        setError('');
        try {
            await mutate(
                `/admin/invoice-obligations/${editId}/scheduled-payments/${serverId}/reconcile`,
                token,
                'PATCH',
                { bank_statement_entry_id: entryId },
            );
            const payRes = await api<{ payments: ScheduledPaymentRow[] }>(
                `/admin/invoice-obligations/${editId}/scheduled-payments`,
                token,
            );
            setPaymentDrafts(paymentDraftsFromServer(payRes.payments));
            setCandidatesByKey((prev) => {
                const next = { ...prev };
                delete next[key];

                return next;
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        } finally {
            setSaving(false);
        }
    }

    async function clearReconcile(serverId: number | null) {
        if (editId == null || serverId == null) {
            return;
        }
        setSaving(true);
        setError('');
        try {
            await mutate(
                `/admin/invoice-obligations/${editId}/scheduled-payments/${serverId}/reconcile`,
                token,
                'PATCH',
                { clear: true },
            );
            const payRes = await api<{ payments: ScheduledPaymentRow[] }>(
                `/admin/invoice-obligations/${editId}/scheduled-payments`,
                token,
            );
            setPaymentDrafts(paymentDraftsFromServer(payRes.payments));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        } finally {
            setSaving(false);
        }
    }

    async function archiveRow(id: number) {
        const ok =
            locale === 'en'
                ? window.confirm('Delete this obligation? It stays reachable under the "Archived" filter and is excluded from the cashflow projection.')
                : window.confirm('Obrisati ovu obvezu? Ostaje dostupna pod filterom "Arhiva" i izlazi iz projekcije cashflowa.');
        if (!ok) {
            return;
        }
        setSaving(true);
        setError('');
        try {
            await mutate(`/admin/invoice-obligations/${id}/archive`, token, 'PATCH', {});
            if (editId === id) {
                setEditId(null);
                setPaymentDrafts([]);
            }
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        } finally {
            setSaving(false);
        }
    }

    async function openPartnerEditor(id: number) {
        try {
            const res = await api<{ partner: PartnerRow }>(`/admin/partners/${id}`, token);
            setPartnerEditor(res.partner);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
        }
    }

    async function duplicateNextMonth(id: number) {
        setSaving(true);
        setError('');
        try {
            await mutate(`/admin/invoice-obligations/${id}/duplicate-next-month`, token, 'POST', {});
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        } finally {
            setSaving(false);
        }
    }

    async function deleteRow(id: number) {
        const ok =
            locale === 'en'
                ? window.confirm('Delete this obligation record (soft delete)?')
                : window.confirm('Obrisati zapis obveze (soft delete)?');
        if (!ok) {
            return;
        }
        setSaving(true);
        setError('');
        try {
            await mutate(`/admin/invoice-obligations/${id}`, token, 'DELETE');
            if (editId === id) {
                setEditId(null);
                setPaymentDrafts([]);
            }
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        } finally {
            setSaving(false);
        }
    }

    const companies = payload?.companies ?? [];
    const counterparties = payload?.counterparties ?? [];
    const rows = payload?.rows ?? [];

    const filteredInvoiceRows = useMemo(() => {
        const q = invoiceTableSearch.trim();
        if (!q) {
            return rows;
        }

        return rows.filter((row) =>
            matchesQuickSearch(
                [
                    row.id,
                    row.public_id,
                    row.company_code,
                    row.counterparty_name,
                    row.document_number,
                    row.incoming_document_reference,
                    row.notes,
                    row.status,
                    row.obligation_kind,
                    row.purchase_order_public_code,
                    row.ledger_status,
                    row.payment_status,
                    row.amount_open,
                    row.amount_total,
                    row.currency_code,
                    row.cost_type,
                    row.document_date,
                    row.due_date,
                    row.created_at,
                    row.updated_at,
                ],
                q,
            ),
        );
    }, [rows, invoiceTableSearch]);

    const t = {
        active: locale === 'en' ? 'Active' : 'Aktivne',
        archived: locale === 'en' ? 'Archived' : 'Arhiva',
        all: locale === 'en' ? 'All' : 'Sve',
        schemaNote:
            locale === 'en'
                ? 'UBL 2.1 Invoice/CreditNote (Croatian e-invoice). Validator & XSD: validate.moj-eracun.hr — same XML can be parsed for payables or receivables depending on this submodule.'
                : 'UBL 2.1 Invoice/CreditNote (hr eRačun). Validator i XSD: validate.moj-eracun.hr — isti XML možete uvesti i kao URA i kao IRA (ovisno o podmodulu).',
        importTitle: locale === 'en' ? 'Import XML / XLSX (deduplicated)' : 'Uvoz XML / XLSX (deduplikacija)',
        plannedTitle: locale === 'en' ? 'Planned payments (amount + date)' : 'Planirana plaćanja (iznos + datum)',
        addPay: locale === 'en' ? 'Add payment line' : 'Dodaj uplatu',
        findBank: locale === 'en' ? 'Match bank statement' : 'Pronađi na izvodu',
        link: locale === 'en' ? 'Link' : 'Veži',
        unlink: locale === 'en' ? 'Unlink' : 'Odvoji',
        cfBtn: locale === 'en' ? 'Open Cashflow' : 'Otvori Cashflow',
        dupNext: locale === 'en' ? '+1 month' : '+1 mjesec',
        colPo: locale === 'en' ? 'PO code' : 'Šifra nar.',
        colLedger: locale === 'en' ? 'Ledger' : 'Knjiga',
        colPayment: locale === 'en' ? 'Paid' : 'Plaćanje',
        colLiq: locale === 'en' ? 'Liq.' : 'Likv.',
        filLedger: locale === 'en' ? 'Ledger' : 'Knjiga',
        filPayment: locale === 'en' ? 'Payment' : 'Plaćanje',
        filLiq: locale === 'en' ? 'Liquidated' : 'Likvidacija',
        filPo: locale === 'en' ? 'PO link' : 'Narudžba',
        poLabel: locale === 'en' ? 'Purchase order code (public)' : 'Šifra narudžbe (javna)',
        allOpt: locale === 'en' ? 'All' : 'Sve',
    };

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🧾</div>
                <div className="bi-module-hero-text">
                    <h1>{title}</h1>
                    <p className="muted">{t.schemaNote}</p>
                </div>
            </div>

            <div className="admin-toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
                <span className="muted">{locale === 'en' ? 'Filter:' : 'Filter:'}</span>
                {(['active', 'archived', 'all'] as StatusTab[]).map((tab) => (
                    <button
                        key={tab}
                        type="button"
                        className={statusTab === tab ? 'compact' : 'secondary compact'}
                        onClick={() => setStatusTab(tab)}
                    >
                        {tab === 'active' ? t.active : tab === 'archived' ? t.archived : t.all}
                    </button>
                ))}
            </div>

            <div className="admin-toolbar" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <label className="filter-label" style={{ marginBottom: 0 }}>
                    <span className="muted">{t.filLedger}</span>
                    <select value={ledgerFilter} onChange={(e) => setLedgerFilter(e.target.value as LedgerFilter)}>
                        <option value="all">{t.allOpt}</option>
                        <option value="not_booked">{locale === 'en' ? 'Not booked' : 'Nije knjiženo'}</option>
                        <option value="booked">{locale === 'en' ? 'Booked' : 'Knjiženo'}</option>
                        <option value="partial">{locale === 'en' ? 'Partial' : 'Djelomično'}</option>
                    </select>
                </label>
                <label className="filter-label" style={{ marginBottom: 0 }}>
                    <span className="muted">{t.filPayment}</span>
                    <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value as PaymentFilter)}>
                        <option value="all">{t.allOpt}</option>
                        <option value="unpaid">{locale === 'en' ? 'Unpaid' : 'Neplaćeno'}</option>
                        <option value="paid">{locale === 'en' ? 'Paid' : 'Plaćeno'}</option>
                        <option value="partial">{locale === 'en' ? 'Partial' : 'Djelomično'}</option>
                    </select>
                </label>
                <label className="filter-label" style={{ marginBottom: 0 }}>
                    <span className="muted">{t.filLiq}</span>
                    <select value={liquidatedFilter} onChange={(e) => setLiquidatedFilter(e.target.value as YesNoAll)}>
                        <option value="all">{t.allOpt}</option>
                        <option value="yes">{locale === 'en' ? 'Yes' : 'Da'}</option>
                        <option value="no">{locale === 'en' ? 'No' : 'Ne'}</option>
                    </select>
                </label>
                <label className="filter-label" style={{ marginBottom: 0 }}>
                    <span className="muted">{t.filPo}</span>
                    <select value={hasPoFilter} onChange={(e) => setHasPoFilter(e.target.value as YesNoAll)}>
                        <option value="all">{t.allOpt}</option>
                        <option value="yes">{locale === 'en' ? 'Linked' : 'Povezano'}</option>
                        <option value="no">{locale === 'en' ? 'None' : 'Bez'}</option>
                    </select>
                </label>
            </div>

            <div className="admin-toolbar" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <QuickSearchInput
                    value={invoiceTableSearch}
                    onChange={setInvoiceTableSearch}
                    placeholder={locale === 'en' ? 'Search table (partner, doc #, amounts, dates…)…' : 'Pretraga tablice (partner, broj, iznosi, datumi…)…'}
                />
            </div>

            {error ? <p className="error">{error}</p> : null}
            {loading ? <p className="muted">{locale === 'en' ? 'Loading…' : 'Učitavam…'}</p> : null}

            {!loading && payload ? (
                <>
                    <AdminCreateCard
                        title={t.importTitle}
                        hint={locale === 'en'
                            ? 'XML: Croatian UBL e-invoice. XLSX: first row headers (broj, datum, iznos, …). Duplicate imports are rejected by fingerprint.'
                            : 'XML: hrvatski UBL eRačun. XLSX: prvi red zaglavlja (broj, datum, iznos, …). Ponovljeni uvoz blokira deduplikacija.'}
                        locale={locale}
                    >
                        <form onSubmit={postImport}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))', gap: '0.85rem', maxWidth: '50rem' }}>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Company' : 'Tvrtka'}
                                    <select value={importCompanyId} onChange={(e) => setImportCompanyId(e.target.value)} required>
                                        <option value="">{locale === 'en' ? 'Select' : 'Odaberi'}</option>
                                        {companies.map((c) => (
                                            <option key={c.id} value={c.id}>{c.code} · {c.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'File (.xml / .xlsx)' : 'Datoteka (.xml / .xlsx)'}
                                    <input
                                        type="file"
                                        accept=".xml,.xlsx,.xls,.csv,application/xml,text/xml"
                                        onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                                    />
                                </label>
                            </div>
                            <div className="admin-toolbar" style={{ marginTop: '0.85rem' }}>
                                <button className="compact" type="submit" disabled={importing || !importFile}>
                                    {locale === 'en' ? 'Import' : 'Uvezi'}
                                </button>
                            </div>
                            {importMessage ? <p className="muted" style={{ marginTop: '0.5rem' }}>{importMessage}</p> : null}
                        </form>
                    </AdminCreateCard>

                    <AdminCreateCard
                        title={locale === 'en' ? 'New record' : 'Novi zapis'}
                        hint={locale === 'en'
                            ? 'Manual entry for an obligation the system did not detect.'
                            : 'Ručni unos obveze koju sustav nije detektirao.'}
                        locale={locale}
                    >
                        <form onSubmit={submitNew}>
                            {lockedKind === 'standing_payment' ? (
                                <label className="filter-label" style={{ display: 'block', marginBottom: '0.85rem' }}>
                                    {locale === 'en' ? 'Virman / contract label (optional)' : 'Opis virmana ili ugovora (opcionalno)'}
                                    <input value={newVirmanNote} onChange={(e) => setNewVirmanNote(e.target.value)} placeholder={locale === 'en' ? 'e.g. utilities, lease…' : 'npr. komunalije, zakup…'} />
                                </label>
                            ) : null}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '0.85rem' }}>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Company' : 'Tvrtka'}
                                    <select value={newCompanyId} onChange={(e) => setNewCompanyId(e.target.value)} required>
                                        <option value="">{locale === 'en' ? 'Select' : 'Odaberi'}</option>
                                        {companies.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Counterparty' : 'Protustranka'}
                                    <select value={newCounterpartyId} onChange={(e) => setNewCounterpartyId(e.target.value)}>
                                        <option value="">—</option>
                                        {counterparties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Document №' : 'Broj dokumenta'}
                                    <input value={newDocNum} onChange={(e) => setNewDocNum(e.target.value)} />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Document date' : 'Datum dokumenta'}
                                    <DateInput value={newDocDate} onChange={setNewDocDate} />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Due date' : 'Datum dospijeća'}
                                    <DateInput value={newDueDate} onChange={setNewDueDate} />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Amount total (EUR)' : 'Ukupno (EUR)'}
                                    <input type="number" step="0.01" value={newAmountTotal} onChange={(e) => setNewAmountTotal(e.target.value)} required />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Amount open (EUR)' : 'Otvoreno (EUR)'}
                                    <input type="number" step="0.01" value={newAmountOpen} onChange={(e) => setNewAmountOpen(e.target.value)} required />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    COGS / OPEX / CAPEX
                                    <input value={newCostType} onChange={(e) => setNewCostType(e.target.value)} placeholder="OPEX" />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Incoming invoice ID (link)' : 'ID ulaznog računa (veza)'}
                                    <input value={newIncomingInvoiceId} onChange={(e) => setNewIncomingInvoiceId(e.target.value)} placeholder="optional" />
                                </label>
                            </div>
                            <label className="filter-label" style={{ display: 'block', marginTop: '0.85rem' }}>
                                {locale === 'en' ? 'Purchase order code (public, optional)' : 'Šifra narudžbe (javna, opcionalno)'}
                                <input
                                    value={newPurchaseOrderCode}
                                    onChange={(e) => setNewPurchaseOrderCode(e.target.value)}
                                    placeholder={locale === 'en' ? 'e.g. 11/1/017/0001' : 'npr. 11/1/017/0001'}
                                    spellCheck={false}
                                    autoComplete="off"
                                />
                            </label>
                            <label className="filter-label" style={{ display: 'block', marginTop: '0.5rem' }}>
                                {locale === 'en' ? 'Notes' : 'Napomena'}
                                <input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} />
                            </label>
                            <div className="admin-toolbar" style={{ marginTop: '0.85rem' }}>
                                <button className="compact" type="submit" disabled={saving}>
                                    {locale === 'en' ? 'Add' : 'Dodaj'}
                                </button>
                            </div>
                        </form>
                    </AdminCreateCard>

                    <div className="table-wrap">
                        <table className="admin-user-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>{locale === 'en' ? 'Co.' : 'Tvrtka'}</th>
                                    <th>{locale === 'en' ? 'Partner' : 'Partner'}</th>
                                    <th>{locale === 'en' ? 'Doc.' : 'Dok.'}</th>
                                    <th>{t.colPo}</th>
                                    <th>{locale === 'en' ? 'Due' : 'Dospijeće'}</th>
                                    <th>{locale === 'en' ? 'Open' : 'Otvoreno'}</th>
                                    <th>{t.colLedger}</th>
                                    <th>{t.colPayment}</th>
                                    <th>{t.colLiq}</th>
                                    <th>{locale === 'en' ? 'Status' : 'Status'}</th>
                                    <th>{locale === 'en' ? 'Created' : 'Kreirano'}</th>
                                    <th>{locale === 'en' ? 'Updated' : 'Ažurirano'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInvoiceRows.map((row) => (
                                    <React.Fragment key={row.id}>
                                    <tr className="interactive-row" style={{ cursor: 'pointer' }} onClick={() => void startEdit(row)} title={locale === 'en' ? 'Open' : 'Otvori'}>
                                        <td>{row.id}</td>
                                        <td>{row.company_code}</td>
                                        <td>{row.counterparty_name || '—'}</td>
                                        <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                            {row.document_date ? formatDate(row.document_date, locale) : '—'}
                                        </td>
                                        <td
                                            className="muted"
                                            style={{
                                                maxWidth: 160,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                            title={row.purchase_order_public_code ?? ''}
                                        >
                                            {row.purchase_order_public_code ?? '—'}
                                        </td>
                                        <td>{row.due_date ? formatDate(row.due_date, locale) : '—'}</td>
                                        <td>{formatMoney(Number(row.amount_open), locale)}</td>
                                        <td>{row.ledger_status ?? '—'}</td>
                                        <td>{row.payment_status ?? '—'}</td>
                                        <td>{row.is_liquidated ? (locale === 'en' ? 'Yes' : 'Da') : locale === 'en' ? 'No' : 'Ne'}</td>
                                        <td>{row.status}</td>
                                        <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                            {row.created_at ? formatDateTime(row.created_at, locale) : '—'}
                                        </td>
                                        <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                            {row.updated_at ? formatDateTime(row.updated_at, locale) : '—'}
                                        </td>
                                    </tr>
                                    {expandedRowId === row.id ? (
                                        <tr>
                                            <td colSpan={14} style={{ background: 'rgba(var(--ad-soft-rgb),0.08)', borderTop: 0, padding: '10px 14px' }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px 18px', fontSize: '0.82rem', color: 'rgba(0,0,0,0.78)' }}>
                                                    <div>
                                                        <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{locale === 'en' ? 'Created by' : 'Kreirao'}</span>
                                                        <div><strong>👤 {row.created_by_name ?? (row.created_by ? `#${row.created_by}` : '—')}</strong>{row.created_at ? <> · {formatDateTime(row.created_at, locale)}</> : null}</div>
                                                    </div>
                                                    {row.updated_at && row.updated_at !== row.created_at ? (
                                                        <div>
                                                            <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{locale === 'en' ? 'Updated by' : 'Ažurirao'}</span>
                                                            <div><strong>✎ {row.updated_by_name ?? (row.updated_by ? `#${row.updated_by}` : '—')}</strong> · {formatDateTime(row.updated_at, locale)}</div>
                                                        </div>
                                                    ) : null}
                                                    <div>
                                                        <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{locale === 'en' ? 'Source' : 'Izvor'}</span>
                                                        <div><strong>{row.source_type === 'manual' ? (locale === 'en' ? '✍ Manual entry' : '✍ Ručni unos') : row.source_type}</strong></div>
                                                    </div>
                                                    {row.purchase_order_public_code ? (
                                                        <div>
                                                            <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{locale === 'en' ? 'Purchase order code' : 'Šifra narudžbe'}</span>
                                                            <div><strong>🔗 {row.purchase_order_public_code}</strong></div>
                                                        </div>
                                                    ) : null}
                                                    {row.incoming_document_reference ? (
                                                        <div>
                                                            <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{locale === 'en' ? 'Linked invoice' : 'Veza na račun'}</span>
                                                            <div><strong>🧾 {row.incoming_document_reference}</strong></div>
                                                        </div>
                                                    ) : null}
                                                    {row.notes ? (
                                                        <div style={{ gridColumn: '1 / -1' }}>
                                                            <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{locale === 'en' ? 'Notes' : 'Napomene'}</span>
                                                            <div style={{ fontStyle: 'italic' }}>{row.notes}</div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    ) : null}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <section className="admin-section-card" style={{ marginTop: '1.5rem' }}>
                        <header style={{ marginBottom: '0.85rem' }}>
                            <h4 style={{ margin: 0 }}>
                                {locale === 'en' ? 'Auto-detected invoices' : 'Sustavno detektirane fakture'}
                                <span className="muted" style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: 8 }}>
                                    (iz {payload?.detected_source ?? '—'}) — top {payload?.detected?.length ?? 0} od {payload?.detected_count ?? 0}
                                </span>
                            </h4>
                            <p className="muted" style={{ marginTop: 4, fontSize: '0.85rem' }}>
                                {locale === 'en'
                                    ? 'Read-only data imported from GFI/ERP. Add manual obligations above for items the system did not detect.'
                                    : 'Read-only uvoz iz GFI/ERP-a. Ručni unos iznad za stavke koje sustav nije detektirao.'}
                            </p>
                        </header>
                        <div className="admin-toolbar" style={{ flexWrap: 'wrap', gap: '0.65rem', alignItems: 'flex-end', marginBottom: '0.85rem' }}>
                            <label className="filter-label" style={{ margin: 0 }}>
                                {locale === 'en' ? 'From' : 'Od datuma'}
                                <DateInput value={detectedDateFrom} onChange={setDetectedDateFrom} />
                            </label>
                            <label className="filter-label" style={{ margin: 0 }}>
                                {locale === 'en' ? 'To' : 'Do datuma'}
                                <DateInput value={detectedDateTo} onChange={setDetectedDateTo} />
                            </label>
                            {invoiceAdminSection === '1212' ? (
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', alignSelf: 'center' }}>
                                    <input type="checkbox" checked={detectedOverdueUnpaid} onChange={(e) => setDetectedOverdueUnpaid(e.target.checked)} />
                                    <span>{locale === 'en' ? 'Overdue & unpaid only' : 'Samo dospjele neplaćene'}</span>
                                </label>
                            ) : null}
                            <button type="button" className="secondary compact" onClick={() => { setDetectedDateFrom(''); setDetectedDateTo(''); setDetectedOverdueUnpaid(false); }}>
                                {locale === 'en' ? 'Clear' : 'Očisti'}
                            </button>
                        </div>
                        {(payload?.detected ?? []).length > 0 ? (
                        <div className="table-wrap">
                            <table className="admin-user-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>{locale === 'en' ? 'Co.' : 'Tvrtka'}</th>
                                        <th>{locale === 'en' ? 'Doc ref' : 'Broj'}</th>
                                        <th>{locale === 'en' ? 'Date' : 'Datum'}</th>
                                        <th>{locale === 'en' ? 'Due' : 'Dospijeće'}</th>
                                        <th>{locale === 'en' ? 'Partner' : 'Partner'}</th>
                                        <th>{locale === 'en' ? 'Amount EUR' : 'Iznos EUR'}</th>
                                        <th>{locale === 'en' ? 'Channel' : 'Kanal'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(payload?.detected ?? []).map((r) => (
                                        <tr key={`det-${r.id}`}>
                                            <td className="muted">{r.id}</td>
                                            <td>{r.company_code}</td>
                                            <td>{r.document_reference ?? '—'}</td>
                                            <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{r.document_date ? formatDate(r.document_date, locale) : '—'}</td>
                                            <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{r.due_date ? formatDate(r.due_date, locale) : '—'}</td>
                                            <td>
                                                <strong>{r.partner_name ?? '—'}</strong>
                                                {r.partner_code ? <p className="muted" style={{ margin: 0, fontSize: '0.75rem' }}>{r.partner_code}</p> : null}
                                            </td>
                                            <td>{r.amount_eur != null ? formatMoney(r.amount_eur, locale) : '—'}</td>
                                            <td className="muted">{r.kanal ?? r.doc_prefix ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        ) : (
                            <p className="muted">{locale === 'en' ? 'No matching detected invoices for the current filters.' : 'Nema detektiranih faktura za trenutne filtere.'}</p>
                        )}
                    </section>

                    {editId != null ? (
                        <div className="bi-oblig-drawer-overlay" onClick={() => setEditId(null)}>
                          <div className="bi-oblig-drawer-panel" onClick={(e) => e.stopPropagation()}>
                            <style>{`
                              .bi-oblig-drawer-overlay { position: fixed; inset: 0; background: rgba(40,28,8,0.34); z-index: 1200; display: flex; justify-content: flex-end; }
                              .bi-oblig-drawer-panel { width: 700px; max-width: 95vw; height: 100%; overflow-y: auto; background: #fffdf8; box-shadow: -14px 0 36px -14px rgba(80,50,10,0.45); padding: 20px 22px 8px; animation: bi-oblig-drawer-in 0.16s ease-out; }
                              @keyframes bi-oblig-drawer-in { from { transform: translateX(28px); opacity: 0.5; } to { transform: none; opacity: 1; } }
                            `}</style>
                            <form className="admin-form-grid" onSubmit={submitEdit}>
                            <h4 style={{ gridColumn: '1 / -1', marginTop: 0 }}>{locale === 'en' ? `Obligation #${editId}` : `Obveza #${editId}`}</h4>
                            <label className="filter-label">
                                {locale === 'en' ? 'Company' : 'Tvrtka'}
                                <select value={editCompanyId} onChange={(e) => setEditCompanyId(e.target.value)} required>
                                    {companies.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.code} · {c.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {lockedKind === 'standing_payment' ? (
                                <label className="filter-label" style={{ gridColumn: '1 / -1' }}>
                                    {locale === 'en' ? 'Virman / contract label' : 'Virman / ugovor (napomena)'}
                                    <input value={editVirmanNote} onChange={(e) => setEditVirmanNote(e.target.value)} />
                                </label>
                            ) : null}
                            <label className="filter-label">
                                {locale === 'en' ? 'Counterparty' : 'Protustranka'}
                                <select value={editCounterpartyId} onChange={(e) => setEditCounterpartyId(e.target.value)}>
                                    <option value="">{locale === 'en' ? '—' : '—'}</option>
                                    {counterparties.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                    {editCounterpartyId ? (
                                        <button type="button" className="secondary compact" onClick={() => void openPartnerEditor(Number(editCounterpartyId))}>✎ {locale === 'en' ? 'Edit partner' : 'Uredi partnera'}</button>
                                    ) : null}
                                    <button type="button" className="secondary compact" onClick={() => setPartnerEditor('new')}>+ {locale === 'en' ? 'New partner' : 'Novi partner'}</button>
                                </div>
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Incoming invoice ID' : 'ID ulaznog računa'}
                                <input value={editIncomingInvoiceId} onChange={(e) => setEditIncomingInvoiceId(e.target.value)} />
                            </label>
                            <label className="filter-label">{locale === 'en' ? 'Document №' : 'Broj dokumenta'}<input value={editDocNum} onChange={(e) => setEditDocNum(e.target.value)} /></label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Document date' : 'Datum dokumenta'}
                                <DateInput value={editDocDate} onChange={setEditDocDate} />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Due date' : 'Dospijeće'}
                                <DateInput value={editDueDate} onChange={setEditDueDate} />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Amount total' : 'Ukupno'}
                                <input type="text" inputMode="decimal" value={editAmountTotal} onChange={(e) => setEditAmountTotal(e.target.value)} onBlur={(e) => setEditAmountTotal(formatMoneyInput(e.target.value))} required />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Amount open' : 'Otvoreno'}
                                <input type="text" inputMode="decimal" value={editAmountOpen} onChange={(e) => setEditAmountOpen(e.target.value)} onBlur={(e) => setEditAmountOpen(formatMoneyInput(e.target.value))} required />
                            </label>
                            <label className="filter-label">
                                COGS / OPEX / CAPEX
                                <input value={editCostType} onChange={(e) => setEditCostType(e.target.value)} />
                            </label>
                            <OrderCodeField
                                token={token}
                                locale={locale}
                                companyId={editCompanyId}
                                value={editPurchaseOrderCode}
                                onChange={setEditPurchaseOrderCode}
                            />
                            <label className="filter-label">
                                {t.colLedger}
                                <select
                                    value={editLedgerStatus}
                                    onChange={(e) =>
                                        setEditLedgerStatus(e.target.value as 'not_booked' | 'booked' | 'partial')
                                    }
                                >
                                    <option value="not_booked">{locale === 'en' ? 'Not booked' : 'Nije knjiženo'}</option>
                                    <option value="booked">{locale === 'en' ? 'Booked' : 'Knjiženo'}</option>
                                    <option value="partial">{locale === 'en' ? 'Partial' : 'Djelomično'}</option>
                                </select>
                            </label>
                            <label className="filter-label">
                                {t.colPayment}
                                <select
                                    value={editPaymentStatus}
                                    onChange={(e) =>
                                        setEditPaymentStatus(e.target.value as 'unpaid' | 'paid' | 'partial')
                                    }
                                >
                                    <option value="unpaid">{locale === 'en' ? 'Unpaid' : 'Neplaćeno'}</option>
                                    <option value="paid">{locale === 'en' ? 'Paid' : 'Plaćeno'}</option>
                                    <option value="partial">{locale === 'en' ? 'Partial' : 'Djelomično'}</option>
                                </select>
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Liquidated' : 'Likvidirano'}
                                <input
                                    type="checkbox"
                                    checked={editLiquidated}
                                    onChange={(e) => setEditLiquidated(e.target.checked)}
                                    style={{ width: 'auto', marginTop: 6 }}
                                />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Record status' : 'Status zapisa'}
                                <select value={editStatus} onChange={(e) => setEditStatus(e.target.value as 'open' | 'archived')}>
                                    <option value="open">open</option>
                                    <option value="archived">archived</option>
                                </select>
                            </label>
                            <label className="filter-label" style={{ gridColumn: '1 / -1' }}>
                                {locale === 'en' ? 'Notes' : 'Napomena'}
                                <input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                            </label>

                            <div style={{ gridColumn: '1 / -1' }}>
                                <h4>{t.plannedTitle}</h4>
                                <p className="muted">
                                    {locale === 'en'
                                        ? 'Cashflow projection uses these dates when at least one line is set; otherwise the due date above is used.'
                                        : 'Projekcija cashflowa koristi ove datume ako postoji barem jedan red; inače se koristi dospijeće gore.'}
                                </p>
                                {paymentDrafts.map((draft) => (
                                    <div key={draft.key} className="admin-toolbar" style={{ flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8 }}>
                                        <label className="filter-label">
                                            {locale === 'en' ? 'Amount' : 'Iznos'}
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={draft.planned_amount}
                                                onChange={(e) =>
                                                    setPaymentDrafts((rows) =>
                                                        rows.map((r) =>
                                                            r.key === draft.key ? { ...r, planned_amount: e.target.value } : r,
                                                        ),
                                                    )
                                                }
                                                onBlur={(e) => {
                                                    const f = formatMoneyInput(e.target.value);
                                                    setPaymentDrafts((rows) => rows.map((r) => (r.key === draft.key ? { ...r, planned_amount: f } : r)));
                                                }}
                                            />
                                        </label>
                                        <label className="filter-label">
                                            {locale === 'en' ? 'Date' : 'Datum'}
                                            <DateInput
                                                value={draft.planned_date}
                                                onChange={(iso) =>
                                                    setPaymentDrafts((rows) =>
                                                        rows.map((r) =>
                                                            r.key === draft.key ? { ...r, planned_date: iso } : r,
                                                        ),
                                                    )
                                                }
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            className="secondary compact"
                                            onClick={() => void loadCandidatesForDraft(draft.key, draft.planned_amount, draft.planned_date)}
                                        >
                                            {t.findBank}
                                        </button>
                                        {draft.bank_statement_entry_id ? (
                                            <span className="muted">
                                                {locale === 'en' ? 'Bank entry' : 'Stavka izvoda'} #{draft.bank_statement_entry_id}
                                            </span>
                                        ) : null}
                                        {draft.bank_statement_entry_id && draft.serverId ? (
                                            <button
                                                type="button"
                                                className="secondary compact"
                                                disabled={saving}
                                                onClick={() => void clearReconcile(draft.serverId)}
                                            >
                                                {t.unlink}
                                            </button>
                                        ) : null}
                                        <button
                                            type="button"
                                            className="secondary compact"
                                            onClick={() =>
                                                setPaymentDrafts((rows) => rows.filter((r) => r.key !== draft.key))
                                            }
                                        >
                                            ×
                                        </button>
                                        {candidatesByKey[draft.key]?.length ? (
                                            <div style={{ width: '100%' }}>
                                                <span className="muted">{locale === 'en' ? 'Candidates:' : 'Kandidati:'}</span>
                                                <div className="admin-toolbar" style={{ flexWrap: 'wrap' }}>
                                                    {candidatesByKey[draft.key].map((c) => (
                                                        <button
                                                            key={c.id}
                                                            type="button"
                                                            className="compact"
                                                            disabled={saving}
                                                            onClick={() => void reconcileDraft(draft.key, draft.serverId, c.id)}
                                                        >
                                                            #{c.id} {c.statement_date ? formatDate(c.statement_date, locale) : '—'}{' '}
                                                            {formatMoney(c.net_amount, locale)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="secondary compact"
                                    onClick={() =>
                                        setPaymentDrafts((rows) => [
                                            ...rows,
                                            {
                                                key: newDraftKey(),
                                                serverId: null,
                                                planned_amount: editAmountOpen,
                                                planned_date: '',
                                                bank_statement_entry_id: null,
                                            },
                                        ])
                                    }
                                >
                                    + {t.addPay}
                                </button>
                            </div>

                            <div className="admin-toolbar" style={{ gridColumn: '1 / -1', position: 'sticky', bottom: 0, background: '#fffdf8', paddingTop: 12, marginTop: 8, borderTop: '1px solid #efe6d2', flexWrap: 'wrap' }}>
                                <button className="compact" type="submit" disabled={saving}>
                                    {locale === 'en' ? 'Save' : 'Spremi'}
                                </button>
                                <button type="button" className="secondary compact" disabled={saving} onClick={() => { if (editId != null) void duplicateNextMonth(editId); }} title={locale === 'en' ? 'Duplicate with due date +1 month' : 'Dupliciraj +1 mjesec dospijeća'}>
                                    {t.dupNext}
                                </button>
                                {onNavigateToCashflow ? (
                                    <button type="button" className="secondary compact" onClick={() => onNavigateToCashflow({ companyIds: [Number(editCompanyId)] })}>
                                        {t.cfBtn}
                                    </button>
                                ) : null}
                                <button type="button" className="secondary compact" disabled={saving} onClick={() => { if (editId != null) void archiveRow(editId); }} style={{ marginLeft: 'auto', color: '#b3261e', borderColor: '#e3b9b0' }}>
                                    {locale === 'en' ? 'Delete' : 'Obriši'}
                                </button>
                                <button type="button" className="secondary compact" onClick={() => setEditId(null)}>
                                    {locale === 'en' ? 'Cancel' : 'Odustani'}
                                </button>
                            </div>
                            </form>
                          </div>
                        </div>
                    ) : null}
                </>
            ) : null}

            {partnerEditor ? (
                <PartnerDrawer
                    token={token}
                    locale={locale}
                    partner={partnerEditor === 'new' ? null : partnerEditor}
                    companies={companies}
                    prefillCompanyId={editCompanyId ? Number(editCompanyId) : null}
                    onClose={() => setPartnerEditor(null)}
                    onSaved={(id) => { setPartnerEditor(null); setEditCounterpartyId(String(id)); void load(); }}
                    setError={setError}
                />
            ) : null}
        </section>
    );
}
